/**
 * The addresses a person owns, and the two kinds they may make for themselves.
 *
 * WHY THIS EXISTS: FOUR CAPABILITIES THAT GATED NOTHING. `addresses.masked` and
 * `addresses.alias` were declared, granted to owner and adult, and named by no
 * `require(...)` anywhere in the origin — so the capability table was a document
 * that lied. The only route to an alias or a disposable address was
 * `admin.server`, which is tailnet-only and hands over the WHOLE warden
 * including `master.rotate`: to let a parent make `bookings@h-group.net` you had
 * to let them rotate the master key from the same panel, and to let them mint a
 * throwaway for a shop you had to put them on the tailnet first. The routes
 * below the origin are what make the two capabilities mean what they say; this
 * section is what makes them reachable, which in this codebase is the difference
 * between a feature and a complete, tested, unpressable one.
 *
 * IT DECIDES NOTHING ABOUT OWNERSHIP. Every route here pins the owner to the
 * principal the origin resolved, and the warden re-checks it against the files
 * it holds: nothing this component sends says whose address it is, and nothing
 * it could send would be believed. That is why there is no owner field on any
 * form here and no filtering of the list — the list arrived already scoped, and
 * a client that filtered it again would be enforcing a rule against data it was
 * handed rather than against the files.
 *
 * THE TWO ACTS ARE NOT ONE ACT, and the layout says so. A named alias is a route
 * into the house that other people come to rely on and that is meant to outlive
 * whoever made it; a disposable address is one you hand to a shop and burn the
 * week it leaks. Under a single heading, "Delete" and "Revoke" become the same
 * button — and only one of them is safe to press quickly.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { api, errorText, forbiddenBy, type AddressBook, type AddressEntry, type Whoami } from './api.ts'

/**
 * The two capability names, spelled once each.
 *
 * They are the ORIGIN'S strings and this file is a reader of them: the same two
 * appear in `require(...)` on the routes and in the role table, and a typo here
 * would not fail — it would quietly hide a control from somebody who holds the
 * capability, which is the least visible way this could go wrong.
 */
const MASKED = 'addresses.masked'
const ALIAS = 'addresses.alias'

/**
 * What this client knows about the caller's addresses, which is only ever what
 * the origin last said.
 *
 * `refused` IS SEPARATE FROM `unreachable` because the remedy is. A 403 is a
 * sentence about this account that will read the same tomorrow; a 500 or a dead
 * network is a reason to try again. Collapsed into one "could not load", a child
 * would be told to retry for ever and a parent on a train would be told their
 * role is wrong.
 */
type Book =
  | { readonly status: 'loading' }
  | { readonly status: 'refused'; readonly why: string }
  | { readonly status: 'unreachable'; readonly why: string }
  | { readonly status: 'read'; readonly book: AddressBook }

/**
 * Whether a control can work HERE, which is three answers and not two.
 *
 * `/whoami` splits `capabilities` from `tailnetOnly` — held and exercisable on
 * this path, versus held by the role and refused on this one — and squashing
 * them into a boolean is what turns "open Hearth over Tailscale and this works"
 * into a control that is simply missing. `addresses.masked` is deliberately NOT
 * tailnet-only, because the entire point is minting one from a phone in a shop;
 * this reads the answer rather than asserting that, so an installation that
 * decides otherwise gets a disabled control with a reason instead of a lie.
 */
type Reach = 'yes' | 'tailnet' | 'no'

export interface AddressesProps {
  /**
   * Who the origin says this is — READ ONCE BY THE SHELL AND PASSED DOWN.
   *
   * This component used to issue its own `GET /whoami` on every open. Two copies
   * of one answer, fetched at different moments, can disagree: a role changed
   * between the two reads gates the command palette one way and this panel
   * another, and the shell's copy models `capabilities` while this one also
   * needs `tailnetOnly` — the same fact with two predicates over it, which is
   * the shape this repo keeps finding. It also cost a round trip per drawer
   * open for something already in memory.
   *
   * `null` MEANS NOT KNOWN, AND NOT-KNOWN GATES NOTHING. `app.tsx` settled that
   * rule for the whole client: a `/whoami` that failed on a flaky network must
   * not paint a reader as having no permissions, because the origin re-checks
   * every capability on every route anyway. The worst case of guessing "yes" is
   * a refusal somebody can read; the worst case of guessing "no" is a household
   * who cannot find a feature they hold.
   */
  readonly me: Whoami | null
}

export function Addresses({ me }: AddressesProps): React.JSX.Element {
  const ids = useId()
  const [view, setView] = useState<Book>({ status: 'loading' })
  /**
   * What the origin has refused SINCE, which outranks `/whoami`.
   *
   * The capability set came from one request and a refusal is the newer fact
   * about the same question — the Screener switch takes a `not_ready` the same
   * way. It carries the reach rather than a bare "no" so that a refusal saying
   * "only available over Tailscale" does not demote the control to unavailable
   * everywhere, which would hide the one sentence that tells somebody the
   * remedy.
   */
  const [denied, setDenied] = useState<ReadonlyMap<string, Reach>>(new Map())
  const [site, setSite] = useState('')
  const [busy, setBusy] = useState(false)
  /**
   * The same fact as `busy`, in a form that is true IMMEDIATELY.
   *
   * `setBusy(true)` schedules a render; it does not change what the next line
   * reads. The mint button is `disabled={busy}` and the site field's Enter key
   * is not, so three Enter presses in one burst all saw `busy === false` and
   * minted three disposable addresses — three real lines in
   * `postfix-virtual.cf`, of which the reader sees only the last, with the other
   * two to be found in the list and revoked one at a time. `disabled` is a
   * property of a button and this is a property of the ACT, which is why the
   * keyboard path could go round it.
   *
   * ONE FUNCTION SETS BOTH, because a ref and a state that mean the same thing
   * are the two-predicates-about-one-thing shape this repo keeps finding. `begin`
   * is the only place either is raised and `end` the only place either is
   * lowered, so they cannot disagree.
   */
  const inFlight = useRef(false)
  const begin = (): boolean => {
    if (inFlight.current) return false
    inFlight.current = true
    setBusy(true)
    return true
  }
  const end = (): void => {
    inFlight.current = false
    setBusy(false)
  }
  /** The address the last mint created, kept where somebody can take it. */
  const [minted, setMinted] = useState<string | null>(null)
  /** Only true once the clipboard has actually accepted it. See `copy`. */
  const [copied, setCopied] = useState(false)
  const [maskedNote, setMaskedNote] = useState<string | null>(null)
  const [aliasNote, setAliasNote] = useState<string | null>(null)

  /**
   * Re-read the list rather than editing the copy on screen.
   *
   * THE SERVER'S LIST IS THE ONE TRUTH ABOUT WHAT EXISTS. Splicing a row out
   * after a revoke, or pushing one in after a mint, would be this component
   * keeping a second account of the `postfix-virtual.cf` the warden owns — and
   * the first refusal it misread would leave an address on screen that no longer
   * routes, or hide one that still does.
   */
  const refresh = useCallback(
    (): Promise<void> =>
      api
        .addresses()
        .then((next) => setView({ status: 'read', book: asBook(next) }))
        .catch((error: unknown) => {
          const forbidden = forbiddenBy(error)
          if (forbidden) {
            deny(setDenied, forbidden.capability, forbidden.tailnetOnly)
            setView({ status: 'refused', why: forbidden.message })
            return
          }
          setView({ status: 'unreachable', why: errorText(error, 'the server did not answer') })
        }),
    [],
  )

  // The panel unmounts when it closes, so this effect IS the on-open read — the
  // same shape the Screener switch uses, and for the same reason: a list cached
  // from an hour ago would offer Revoke on an address somebody has already burnt.
  useEffect(() => {
    void refresh()
  }, [refresh])


  const reach = (capability: string): Reach => {
    const refused = denied.get(capability)
    if (refused) return refused
    // AN ANSWER WITHOUT THE TWO LISTS IS NOT AN ANSWER ABOUT CAPABILITIES.
    // `request<T>` casts and does not check, so these are a promise rather than
    // a fact: reading `.includes` off the `undefined` an origin mid-deploy
    // answered with threw during render, and a component that throws during
    // render takes its whole tree with it — here, the settings drawer, so one
    // unexpected field on one answer emptied the palette, the text-size ladder
    // and the Screener switch off the screen.
    if (!me || !Array.isArray(me.capabilities) || !Array.isArray(me.tailnetOnly)) return 'yes'
    if (me.capabilities.includes(capability)) return 'yes'
    return me.tailnetOnly.includes(capability) ? 'tailnet' : 'no'
  }

  /**
   * One failure path for every button here.
   *
   * IT PRINTS THE ORIGIN'S SENTENCE AND NOT A LOCAL ONE. A refusal names the
   * field and the reason — "that address is not on the masked subdomain", "site
   * is required" — and the rule it is about is held on the other side. A second
   * copy of the wording here would be one edit away from describing a rule that
   * is no longer the one that fired.
   *
   * A REFUSAL ALSO PUTS THE CONTROL AWAY. If the origin has just said the
   * capability is not granted, leaving the button lit invites the same click and
   * the same sentence for as long as somebody is willing to try.
   */
  const failed = (error: unknown, say: (text: string) => void): void => {
    const forbidden = forbiddenBy(error)
    if (forbidden) {
      deny(setDenied, forbidden.capability, forbidden.tailnetOnly)
      say(forbidden.message)
      return
    }
    say(errorText(error, 'the server refused it'))
  }

  const mint = (): void => {
    // THE GUARD IS IN THE ACTION, because that is what the button and the Enter
    // key share. See `inFlight`.
    if (!begin()) return
    setMaskedNote(null)
    setCopied(false)
    api
      .mintMasked(site)
      .then(async (made) => {
        setMinted(made.address)
        // CLEARED ONLY AFTER IT WORKED. A refusal about the hint — "that does not
        // name a site" — beside an empty box is a refusal about text nobody can
        // see any more.
        setSite('')
        await refresh()
      })
      .catch((error: unknown) => failed(error, setMaskedNote))
      .finally(end)
  }

  const revoke = (address: string): void => {
    if (!begin()) return
    setMaskedNote(null)
    api
      .revokeMasked(address)
      .then(async () => {
        // THE BIG BOX MUST NOT OUTLIVE THE ADDRESS IN IT. Everything about that
        // box says "paste this into a signup"; keeping it after the address has
        // been burnt is the one sentence it could say that is false.
        if (address === minted) setMinted(null)
        await refresh()
      })
      .catch((error: unknown) => failed(error, setMaskedNote))
      .finally(end)
  }

  const remove = (address: string): void => {
    if (!begin()) return
    setAliasNote(null)
    api
      .deleteAlias(address)
      .then(() => refresh())
      .catch((error: unknown) => failed(error, setAliasNote))
      .finally(end)
  }

  /**
   * Put the address on the clipboard, and only claim so if it went.
   *
   * THE CLIPBOARD IS NEVER THE ONLY COPY. It is absent over plain HTTP and in
   * some embedded webviews, and `writeText` rejects outright when the document
   * is not focused — so a button that said "Copied" on being pressed would be
   * lying to precisely the reader whose browser did not take it. The address is
   * selectable text above it either way, which is the fallback that always works.
   */
  const copy = (): void => {
    if (!minted) return
    const clipboard = navigator.clipboard
    if (!clipboard) {
      setCopied(false)
      return
    }
    void clipboard.writeText(minted).then(
      () => setCopied(true),
      () => setCopied(false),
    )
  }

  const mine = me?.account ?? null
  const maskedReach = reach(MASKED)
  const aliasReach = reach(ALIAS)

  return (
    <section className="settings-group" aria-labelledby={`${ids}-title`}>
      <h3 className="settings-legend" id={`${ids}-title`}>
        Addresses
      </h3>

      {view.status === 'loading' ? <p className="settings-note">Reading your addresses…</p> : null}
      {view.status === 'unreachable' ? (
        <p className="settings-note">Your addresses could not be read: {view.why}.</p>
      ) : null}
      {/* THE ORIGIN'S SENTENCE, VERBATIM. It already says which capability and
          whether the tailnet would help; a rewrite here would be this client
          having an opinion about a rule it does not hold. */}
      {view.status === 'refused' ? <p className="settings-note">{view.why}</p> : null}

      {/* THE ADDRESS THAT WAS JUST MINTED, OUTSIDE EVERY OTHER GATE.
          It lived inside `view.status === 'read'`, and a mint is followed by a
          re-read: when that re-read failed, `view` became `unreachable` and the
          box holding the address VANISHED — a live address, written to
          `postfix-virtual.cf`, that the reader never saw, under a sentence
          reading "your addresses could not be read", which they can only take as
          "the mint failed". The address outliving the list is the whole reason
          it cannot be rendered as part of one.

          THE LIVE REGION IS PERMANENT AND THE CONTENT IS NOT. A `role="status"`
          element added to the DOM together with its text is the one case screen
          readers are not required to announce; the region has to be there first
          for the change to be a change. So the paragraph is always rendered and
          is empty until there is something to say. */}
      <p className="address-minted" role="status" data-empty={minted === null ? 'yes' : 'no'}>
        {minted === null ? null : (
          <>
            <code className="address-minted-value">{minted}</code>
            <button type="button" className="address-copy" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
            <span className="address-minted-note">
              It is live now. Paste it into the signup; replies go straight from it, and it is in the list below if you
              close this.
            </span>
          </>
        )}
      </p>

      {view.status === 'read' ? (
        <>
          <div className="address-block">
            <h4 className="address-heading">Named addresses</h4>
            <AddressList
              entries={view.book.aliases}
              empty="You have no named addresses."
              action={aliasReach === 'yes' ? { label: 'Delete', on: remove, busy } : null}
            />
            {/* THERE IS NO "NEW NAMED ADDRESS" FORM, AND ITS ABSENCE IS THE
                DESIGN RATHER THAN A GAP. A first draft of this panel had one.
                `postfix-virtual.cf` is `virtual_alias_maps`, which OUTRANKS
                REAL ACCOUNTS, so a form that took an address and made it
                deliver here was a form that could take a household member's own
                address and deliver their mail here instead — pinning the
                destination does not help, because the danger is the SOURCE. A
                name on the household's domain is a route other people come to
                rely on; making one is an administrator's act, on the Server
                screen, where a diff is read before a file changes.
                `docs/drafts/addresses.md` §7 says the same. What is offered
                here is what is safe and reversible: seeing yours, and removing
                one that delivers to you and to nobody else. */}
            <p className="settings-note">
              {aliasNote ??
                (aliasReach === 'no'
                  ? 'A named address is a route into the house, so changing one is not something every account may do.'
                  : aliasReach === 'tailnet'
                    ? 'Named addresses can only be changed over Tailscale on this installation.'
                    : // ONE SENTENCE WITH A HOLE IN IT, not two sentences that
                      // differ by a clause: `/whoami` may not have answered yet,
                      // and a second copy of this wording for that case is one
                      // edit from telling two different households two different
                      // rules about the same thing.
                      `These deliver to ${mine ?? 'your own mailbox'}. Making a new one is an administrator’s job, on the Server screen; you can remove one here when it delivers to you and to nobody else.`)}
            </p>
          </div>

          <div className="address-block">
            <h4 className="address-heading">Disposable addresses</h4>
            {view.book.maskedDomain === null ? (
              /* NO FORM AT ALL, AND THE REASON IN PLAIN WORDS. Disposable
                 addresses live on their own subdomain so a leak never exposes
                 the household domain; without one configured there is nothing to
                 mint on, and every press of a button here would end in a refusal
                 naming an environment variable the reader has never heard of. */
              <p className="settings-note">
                This installation has no separate domain for disposable addresses, so there are none to make. They need
                a subdomain of their own — that is a change on the server, not something to switch on here.
              </p>
            ) : (
              <>
                <AddressList
                  entries={view.book.masked}
                  empty="You have no disposable addresses."
                  action={maskedReach === 'yes' ? { label: 'Revoke', on: revoke, busy } : null}
                />
                {maskedReach === 'yes' ? (
                  <div className="address-form">
                    <label className="address-field" htmlFor={`${ids}-site`}>
                      <span>For which site (optional)</span>
                      <input
                        id={`${ids}-site`}
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="petco.com"
                        value={site}
                        onChange={(event) => setSite(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault()
                            mint()
                          }
                        }}
                      />
                    </label>
                    <button type="button" className="address-do" disabled={busy} onClick={mint}>
                      New disposable address
                    </button>
                  </div>
                ) : null}
                <p className="settings-note">
                  {maskedNote ??
                    (maskedReach === 'no'
                      ? 'Your account may not make disposable addresses.'
                      : maskedReach === 'tailnet'
                        ? 'Disposable addresses can only be made over Tailscale on this installation.'
                        : `They are made on ${view.book.maskedDomain}, so one that leaks never exposes the household’s own domain. The site is only a hint written into the address so you can tell them apart later; it does not stop anyone else sending there. Revoking one refuses mail at the door and the sender’s own server says so — it is never silently dropped, so a signup that still needed the address fails where somebody can see it rather than leaving them waiting for mail that was accepted and deleted.`)}
                </p>
              </>
            )}
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * The answer, read field by field rather than trusted whole.
 *
 * THE SAME LESSON AS `who` ABOVE, and the one Admin.tsx already learned about
 * `maskedDomain` on `/health`: `request<AddressBook>` is a cast, so every field
 * here is a promise about a body somebody else's code writes. A list that came
 * back missing turned `entries.length` into a throw during render and took the
 * entire settings drawer down with it — and the client and the origin ship
 * separately, so "the route is not there yet" is a state that will actually
 * happen rather than a hypothetical.
 *
 * A MISSING `maskedDomain` IS READ AS "NONE" and not as "did not say". The two
 * differ only for an origin that does not implement this route, where every
 * mint would be refused anyway; treating it as absent shows the sentence about
 * having no subdomain and no form, which is exactly what a reader can do
 * nothing about either way. Guessing the other direction would offer a button
 * whose only outcome is a refusal.
 */
function asBook(answer: AddressBook): AddressBook {
  // WIDENED BEFORE IT IS READ, deliberately. Against the declared `AddressBook`
  // every check below is dead code to a compiler that has been told the fields
  // are there — and that confidence is exactly what put a throw in the render
  // path. `Partial` is the honest description of a body somebody else wrote.
  const body = answer as Partial<AddressBook> | null | undefined
  return {
    maskedDomain: typeof body?.maskedDomain === 'string' ? body.maskedDomain : null,
    aliases: asEntries(body?.aliases),
    masked: asEntries(body?.masked),
  }
}

function asEntries(value: unknown): readonly AddressEntry[] {
  if (!Array.isArray(value)) return []
  const entries: AddressEntry[] = []
  for (const item of value as readonly Partial<AddressEntry>[]) {
    if (typeof item?.source !== 'string') continue
    const destinations: unknown[] = Array.isArray(item.destinations) ? item.destinations : []
    // AN ENTRY WITH NO RIGHT-HAND COLUMN IS KEPT, not filtered away. A route
    // into the house that delivers nowhere is worth somebody seeing; the list
    // says "delivers nowhere" beside it, and a silent drop would hide the one
    // row that needs attention.
    entries.push({
      source: item.source,
      destinations: destinations.filter((destination): destination is string => typeof destination === 'string'),
      // ONLY A LITERAL `false` MEANS NO. The field is the server saying it would
      // refuse to take this line away — a catch-all, a local part in capitals,
      // anything `checkAddress` will not write — and an origin older than the
      // field says nothing at all. Reading anything-but-true as "no" would take
      // every control on this panel away against a server that never said so,
      // which is the same failure as offering one that cannot work, pointing
      // the other way.
      removable: item.removable !== false,
      // THE ACTIVITY IS COPIED ONLY WHEN IT IS THE SHAPE IT CLAIMS TO BE.
      // `request<T>` casts rather than checks, and this render path has already
      // been taken down once by a field that was not what its type said —
      // `toLocaleString` on a string, `.map` on an object. An origin that does
      // not send these leaves them absent, and the row simply says nothing about
      // traffic, which is what it said before the origin learned to.
      ...(typeof item.messages === 'number' ? { messages: item.messages } : {}),
      ...(typeof item.lastAt === 'number' ? { lastAt: item.lastAt } : {}),
      ...(Array.isArray(item.senderDomains)
        ? {
            senderDomains: (item.senderDomains as readonly { domain?: unknown; messages?: unknown }[])
              .filter((one) => typeof one?.domain === 'string')
              .map((one) => ({
                domain: one.domain as string,
                messages: typeof one.messages === 'number' ? one.messages : 0,
              })),
          }
        : {}),
      ...(typeof item.senderDomainsTotal === 'number' ? { senderDomainsTotal: item.senderDomainsTotal } : {}),
      ...(typeof item.forDomain === 'string' ? { forDomain: item.forDomain } : {}),
      ...(Array.isArray(item.leakedFrom)
        ? {
            leakedFrom: (item.leakedFrom as readonly unknown[]).filter(
              (one): one is string => typeof one === 'string',
            ),
          }
        : {}),
    })
  }
  return entries
}

/**
 * One list of addresses, with at most one thing you can do to each.
 *
 * THE BUTTON'S NAME CARRIES THE ADDRESS. Five rows of "Revoke" are five
 * identically-named controls to a screen reader, and the row a person means is
 * exactly the thing they cannot then hear — while a mis-press here burns an
 * address somebody has already handed to a shop.
 */
function AddressList({
  entries,
  empty,
  action,
}: {
  entries: readonly AddressEntry[]
  empty: string
  action: { label: string; on: (address: string) => void; busy: boolean } | null
}): React.JSX.Element {
  if (entries.length === 0) return <p className="settings-note">{empty}</p>
  return (
    <ul className="address-list">
      {entries.map((entry) => (
        <li className="address-row" key={entry.source}>
          <span className="address-source">{entry.source}</span>
          <span className="address-to">{deliversTo(entry)}</span>
          {/* WHAT HAS ARRIVED HERE, which is the whole basis of a decision to
              revoke. A list of eleven disposable addresses with nothing but the
              strings on it is eleven indistinguishable rows and a button that
              destroys one of them; "12 messages, last in March, from petco.com
              and acmemarketing.net" is the judgement with the evidence in it —
              and the second domain, on an address minted for a pet shop, is the
              leak, visible without anything having to call it one. */}
          {typeof entry.messages === 'number' ? (
            <span className="address-traffic">{trafficOf(entry)}</span>
          ) : null}
          {/* THE ONE SENTENCE THIS PRODUCT CAN SAY THAT NO OTHER WEBMAIL DOES.
              An address handed to one shop that hears from an ad network was
              sold or breached, and the reader is one press from ending it.
              `role="status"` rather than `alert`: it is true of a row in a list
              somebody opened deliberately, not an interruption. */}
          {entry.leakedFrom && entry.leakedFrom.length > 0 ? (
            <strong className="address-leak" role="status">
              {`You made this for ${entry.forDomain}. ${entry.leakedFrom.join(', ')} ${
                entry.leakedFrom.length === 1 ? 'has' : 'have'
              } written to it, which means it got out.`}
            </strong>
          ) : null}
          {/* `removable === false` IS THE SERVER SAYING IT WOULD REFUSE, and a
              button that answers every press with the same 400 is worse than no
              button. A row can still be shown — it is a real route into the
              house, and the person it delivers to should see that it exists —
              which is why this hides the control and not the line.
              ABSENT MEANS YES: an origin older than the field is a working
              origin, and defaulting to "no" would take every control away
              against a server that never said anything. */}
          {action && entry.removable !== false ? (
            <button
              type="button"
              className="address-act"
              aria-label={`${action.label} ${entry.source}`}
              disabled={action.busy}
              onClick={() => action.on(entry.source)}
            >
              {action.label}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/**
 * What has arrived at one address, as a sentence somebody can act on.
 *
 * NOTHING IS THE MOST INFORMATIVE ANSWER THERE IS on this screen, and it gets
 * said rather than left blank: an address minted last week that has never been
 * written to is either a signup that failed or one that has not started, and
 * both are things the person who minted it wants to know.
 *
 * THE DOMAINS ARE NAMED AND NOT COUNTED. "3 senders" is a number nobody can act
 * on; `petco.com` beside `acmemarketing.net` is the leak, and the reader sees it
 * without this file having to decide what a leak is — which it cannot, because
 * the site an address was minted FOR is not stored anywhere and the truncated
 * hint in the local part reduces `email.petco.com` to `email`.
 */
function trafficOf(entry: AddressEntry): string {
  const messages = entry.messages ?? 0
  if (messages === 0) return 'nothing has arrived here yet'
  const when = typeof entry.lastAt === 'number' ? `, last ${new Date(entry.lastAt * 1000).toLocaleDateString()}` : ''
  const listed = entry.senderDomains ?? []
  const total = entry.senderDomainsTotal ?? listed.length
  // THE TAIL IS A NUMBER, and it is there so the named few are not read as all
  // of them: an address that has been sold on hears from hundreds.
  const rest = total > listed.length ? ` and ${(total - listed.length).toLocaleString()} more` : ''
  const from = listed.length === 0 ? '' : ` from ${listed.map((one) => one.domain).join(', ')}${rest}`
  return `${messages.toLocaleString()} ${messages === 1 ? 'message' : 'messages'}${when}${from}`
}

/**
 * Where one address delivers, as a clause.
 *
 * THE EMPTY CASE IS SAID RATHER THAN RENDERED BLANK. A line in
 * `postfix-virtual.cf` with nothing in its right-hand column is one Postfix does
 * not route, and "delivers to" with nothing after it reads as a broken screen
 * rather than as a broken alias — which is exactly backwards, because the second
 * one is worth somebody's attention.
 */
function deliversTo(entry: AddressEntry): string {
  return entry.destinations.length === 0 ? 'delivers nowhere' : `delivers to ${entry.destinations.join(', ')}`
}

/**
 * Record what the origin has just refused, without losing WHERE it would work.
 *
 * A refusal with no capability name — the listener's own 403, or a warden
 * refusal forwarded verbatim — changes nothing here on purpose: switching a
 * control off on a guessed name would take away one the origin never mentioned.
 */
function deny(
  set: (update: (current: ReadonlyMap<string, Reach>) => ReadonlyMap<string, Reach>) => void,
  capability: string | null,
  tailnetOnly: boolean,
): void {
  if (!capability) return
  set((current) => new Map(current).set(capability, tailnetOnly ? 'tailnet' : 'no'))
}

